<template>
  <div class="timetable-week show-time">
    <div class="timetable-axis">
      <div
        class="axis-item"
        v-for="hour in hours"
        v-bind:style="{ height: rowHeight + 'px' }"
      >
        {{ hour }}:00
      </div>
    </div>
    <div class="timetable-columns">
      <div
        class="timetable-column"
        v-for="(date, di) in dates"
        v-bind:class="{
          today: date.isToday,
          'vacation-select-mode': vacationSelectMode,
          'vacation-day-selected': isVacationDaySelected(date),
        }"
        :data-col-index="di"
      >
        <div
          class="timetable-column-header"
          @click="vacationSelectMode && toggleVacationDay(date)"
        >
          <div class="timetable-column-header-date">
            {{ date.format("ddd") }}<br />
            <span @click.stop.prevent="vacationSelectMode ? toggleVacationDay(date) : openDayView(date)">{{
              date.format("D")
            }}</span>
          </div>
          <!--
            A second way into the holiday, in the one place events cannot reach. The
            button on the block itself sits in the grid, where a lesson at the first
            visible hour paints straight over it; the header is always clear. Hidden
            while picking days, when every click in the column selects the day instead.
          -->
          <button
            v-if="!vacationSelectMode && canManageVacations && vacationsForDate(date).length"
            type="button"
            class="vacation-header-btn"
            :title="texts.vacation_manage || 'Gestionează concediul'"
            @click.stop.prevent="onVacationHeaderClick(date)"
          >
            <i class="icon-pencil"></i>
          </button>
        </div>
        <!--
          Sibling of the header, not a child: .show-time clips the header to a fixed
          height, so a list opened inside it would be cut off after a few pixels.
        -->
        <ul class="vacation-header-list" v-if="isVacationMenuOpen(date)">
          <li class="vacation-header-list-title">
            {{ texts.reservations || "Rezervări" }}
          </li>
          <li v-for="vacation in vacationsForDate(date)">
            <button type="button" @click.stop.prevent="pickVacation(vacation)">
              {{ vacationLabel(vacation) }}
            </button>
          </li>
        </ul>
        <div class="timetable-column-content">
          <div
            v-if="isHoverSlotOn(date)"
            class="timetable-drop-placeholder hover-placeholder"
            v-bind:class="{ 'is-picked': hoverSlot.picked }"
            v-bind:style="hoverSlotStyle()"
          ></div>
          <div
            class="timetable-item"
            v-for="event in eventTemplatesByDates[date]"
            v-if="action !== 'show' && showEventTemplate(event, date)"
          >
            <a
              class="timetable-title color-5"
              v-bind:class="{
                disabled: event._start_hour >= blockSchedulingAfterHour,
                compact: isCompactBlock(event._duration_in_hours),
              }"
              @pointerdown="vacationSelectMode || onTemplatePointerDown(event, date, $event)"
              @click.stop.prevent="
                vacationSelectMode
                  ? toggleVacationDay(date)
                  : clickEventTemplate(event, date, $event)
              "
              v-bind:style="{
                top: rowHeight * (event._offset - calendarStartHour) + 'px',
                height: rowHeight * event._duration_in_hours + 'px',
              }"
            >
              <div class="timetable-title-wrap">
                <div class="timetable-name">
                  {{ eventLabel(event) }}
                </div>
                <div class="timetable-time">
                  {{ event._start_hour }}:{{ event._start_minute | pad }} -
                  {{ event._end_hour }}:{{ event._end_minute | pad }}
                </div>
              </div>
            </a>
          </div>

          <div
            class="timetable-item"
            v-for="event in eventsByDates[date]"
            v-bind:class="event.custom_css_classes"
            v-if="showEvent(event)"
          >
            <a
              class="timetable-title color"
              v-bind:class="{
                'color-1 practice-lesson': event.type === 'practice-lesson',
                'color-2': event.type === 'internal-exam',
                'color-4': event.type === 'theory-lesson',
                'color-0': event.type === 'custom-event',
                'color-3 vacation': isBlockingEvent(event),
                reservation: isReservationEvent(event),
                'is-manageable':
                  isBlockingEvent(event) &&
                  canManageVacations &&
                  !vacationSelectMode,
                selected: isEventHighlighted(event),
                disabled: event.disabled,
                'is-dragging': drag && drag.event === event && drag.started,
                'is-draggable': isDraggableEvent(event),
                compact: !isBlockingEvent(event) && isCompactBlock(event._duration_in_hours),
              }"
              @pointerdown="onEventPointerDown(event, $event)"
              @touchstart="onEventTouchStart(event, $event)"
              @contextmenu="onEventContextMenu(event, $event)"
              :title="eventLabel(event)"
              @click.stop.prevent="
                vacationSelectMode
                  ? toggleVacationDay(date)
                  : onMaybeClick(event, date, $event)
              "
              v-bind:style="eventBlockStyle(event, date)"
            >
              <button
                v-if="isBlockingEvent(event) && canManageVacations"
                type="button"
                class="vacation-manage-btn"
                :title="texts.vacation_manage || 'Gestionează concediul'"
                @click.stop.prevent="manageVacation(event)"
              >
                <i class="icon-pencil"></i>
              </button>
              <div class="timetable-title-wrap">
                <div
                  class="timetable-name"
                  :style="{ '--tt-name-lines': nameLineClamp(event._duration_in_hours) }"
                >
                  <hero-icon v-if="isReservationEvent(event)" name="lock-closed" class="tt-block-icon"></hero-icon>
                  <hero-icon v-else-if="isBlockingEvent(event)" name="sun" class="tt-block-icon"></hero-icon>
                  {{ eventLabel(event) }}
                </div>
                <div class="timetable-time">
                  {{ event._start_hour }}:{{ event._start_minute | pad }} -
                  {{ event._end_hour }}:{{ event._end_minute | pad }}
                </div>
              </div>
            </a>
          </div>
        </div>
        <div class="timetable-column-grid" @mouseleave="leaveGrid">
          <div
            class="grid-item cursor-plus"
            v-for="hour in hours"
            @mousemove="hoverNewEventSlot(date, hour, $event)"
            @pointerdown="vacationSelectMode || onGridPointerDown(date, $event)"
            @click="
              vacationSelectMode
                ? toggleVacationDay(date)
                : addNewEvent(date, hour, $event)
            "
            v-bind:style="{ height: rowHeight + 'px' }"
          ></div>
        </div>
      </div>
    </div>
    <slot-menu
      v-if="slotMenu"
      :x="slotMenu.x"
      :y="slotMenu.y"
      :label="slotMenuLabel()"
      :texts="texts"
      @pick="pickSlotAction"
      @close="closeSlotMenu"
    ></slot-menu>
  </div>
</template>

<script>
import TimetableViewBase from "./TimetableView-Base.vue";
import { DragRescheduleMixin } from "./mixins/DragRescheduleMixin";
import HeroIcon from "./HeroIcon.vue";

export default {
  extends: TimetableViewBase,
  mixins: [DragRescheduleMixin],
  components: { "hero-icon": HeroIcon },

  props: {
    vacationSelectMode: {
      default: false,
    },
    selectedVacationDays: {
      default: () => [],
    },
    canManageVacations: {
      default: false,
    },
  },

  data() {
    return {
      view: "week",
      // Day whose holiday list is open, as YYYY-MM-DD; null when none is.
      vacationMenuKey: null,
    };
  },

  mounted() {
    console.log("Timetable View Week Component Mounted.");
    // The list's own buttons stop propagation, so this only ever sees clicks
    // landing outside it.
    document.addEventListener("click", this.closeVacationMenu);
  },

  beforeDestroy() {
    document.removeEventListener("click", this.closeVacationMenu);
  },

  methods: {
    isVacationDaySelected(date) {
      return this.selectedVacationDays.indexOf(moment(date).format("YYYY-MM-DD")) >= 0;
    },

    toggleVacationDay(date) {
      this.eventBus.$emit("toggle-vacation-day", moment(date).toDate());
    },

    isVacationMenuOpen(date) {
      return this.vacationMenuKey === moment(date).format("YYYY-MM-DD");
    },

    closeVacationMenu() {
      this.vacationMenuKey = null;
    },

    onVacationHeaderClick(date) {
      const vacations = this.vacationsForDate(date);

      // One holiday needs no menu -- naming a single option costs a click and
      // tells the user nothing they cannot already see in the column.
      if (vacations.length === 1) {
        this.closeVacationMenu();
        this.manageVacation(vacations[0]);
        return;
      }

      this.vacationMenuKey = this.isVacationMenuOpen(date)
        ? null
        : moment(date).format("YYYY-MM-DD");
    },

    pickVacation(vacation) {
      this.closeVacationMenu();
      this.manageVacation(vacation);
    },

    // Mirrors the label on the block itself, so the menu entry and the thing it
    // opens read the same.
    vacationLabel(vacation) {
      if (vacation.is_full_day_event) {
        return this.texts.vacation_all_day || "Toată ziua";
      }

      const pad = (value) => (value < 10 ? "0" + value : value);

      return (
        vacation._start_hour + ":" + pad(vacation._start_minute) +
        " - " +
        vacation._end_hour + ":" + pad(vacation._end_minute)
      );
    },
  },
};
</script>
<style lang="scss">
</style>
